import PropTypes from 'prop-types'
import ContentfulJsonLdBlocks from '../src/components/shared/ContentfulJsonLdBlocks'
import React from 'react'
import dynamic from 'next/dynamic'
import Layout from '../src/components/layouts'
import DocumentPageLayout from '../src/components/shared/DocumentPageLayout'
import AuthorsService from '../src/services/AuthorsService'
import { loadPageData } from 'src/common/loadPageData'
import {
  collectRecommendedReadPaths,
  filterPublishedRecommendedReads,
} from 'src/common/recommendedReads'
import PageDataService from 'src/services/PageDataService'
import { getFullUrlFromReq } from '../src/common/serverUtils'
import { generateSeoData } from '../src/common/nextSeoUtils'
import { CONTENT_TYPES } from '../src/common/constants'
import { NON_BRAND_CONTENT_TYPES } from 'src/common/constants/prospectQuizNudge'
import { preferRedirects, serveFileFromUrl } from '../src/common/back/helpers'
import recommended from '../src/common/constants/linking.json'
import logger from '../src/common/logger'
import WithSeoOverrides from 'src/components/shared/WithSeoOverride'
import { getMegamenuData } from 'src/common/back/megamenu'
import { resolveContentfulJsonLdFromPageData } from 'src/common/getContentfulJsonLd'
import { getPageTrustBundle } from 'src/common/getPageTrustBundle'
import { resolvePasswordProtection } from 'src/common/passwordProtection'
import PasswordProtectedGate from 'src/components/shared/PasswordProtectedGate'

// Memoize dynamic imports to prevent unnecessary re-imports
const layoutsByType = {
  [CONTENT_TYPES.DOCUMENT_PAGES]: DocumentPageLayout,
  [CONTENT_TYPES.ARTICLE_PAGES]: dynamic(
    () => import('../src/components/layouts/ArticleLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.BLOG_PAGES]: dynamic(
    () => import('../src/components/layouts/BlogLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.DEBT_CONSOLIDATION_STATES_CITIES]: dynamic(
    () => import('../src/components/pages/DebtConsolidationState'),
    { ssr: true }
  ),
  [CONTENT_TYPES.DISCLAIMER_PAGE_LAYOUT]: dynamic(
    () => import('../src/components/shared/DisclaimerPageLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.CLIENT_STORIES]: dynamic(
    () => import('../src/components/shared/ClientStoryLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.IFRAMES_PAGES]: dynamic(
    () => import('../src/components/shared/IFramePageLayout'),
    { ssr: true }
  ),
}

const isInternalNextPath = (pid = []) =>
  pid[0] === '_next' || pid.some((segment) => segment.includes('.hot-update.'))

async function resolvePublishedRecommendedReads(path, preview) {
  const reads = recommended[path] || {}
  const paths = collectRecommendedReadPaths(reads)

  if (preview || !paths.length) {
    return reads
  }

  try {
    const publishedPaths =
      await PageDataService.getInstance().getPublishedPaths(paths)

    return filterPublishedRecommendedReads(reads, publishedPaths)
  } catch (error) {
    logger.error(
      'DynamicPage: failed to filter unpublished recommended reads',
      {
        path,
        error,
      }
    )

    return { ...reads, top: [], bottom: [] }
  }
}

export const getServerSideProps = preferRedirects(async (context) => {
  const { params, req, res, preview } = context

  if (isInternalNextPath(params.pid)) {
    return { notFound: true }
  }

  const path = `${params.pid?.join('/')}/`
  let megamenuData = null

  try {
    const [loadedPageData, trustBundle] = await Promise.all([
      loadPageData(path, preview),
      getPageTrustBundle(preview),
    ])
    const { restrictionValue, isRestricted, ...pageData } = loadedPageData
    const passwordProtection = resolvePasswordProtection({
      entryId: pageData.key,
      isRestricted,
      restrictionValue,
      cookieHeader: req.headers.cookie,
    })

    const { trustMarkersCombination, trustPilotInfo, reviews } = trustBundle
    const {
      rating: tpRating,
      link: tpLink,
      totalReview: tpTotal,
    } = trustPilotInfo

    const isFilePage = pageData?.model?.contentType === 'wwwFile'

    // Early return for invalid pages
    if (
      !pageData?.model?.key ||
      (!isFilePage && !layoutsByType[pageData?.model?.contentType])
    ) {
      return { notFound: true }
    }

    if (passwordProtection.isProtected) {
      res?.setHeader('Cache-Control', 'private, no-store')
      res?.setHeader('Cloudflare-CDN-Cache-Control', 'no-store')
    }

    if (isFilePage && !passwordProtection.isLocked) {
      await serveFileFromUrl(res, {
        url: pageData.model.fileAsset?.url,
        contentType: pageData.model.fileAsset?.contentType,
        fileName: pageData.model.fileAsset?.fileName,
      })

      return { props: {} }
    }

    const locale = pageData?.locale?.toLowerCase() === 'spanish' ? 'es' : 'en'
    const { canonicalUrl } = getFullUrlFromReq(req, params.pid)

    const contentfulJsonLdSchemas = passwordProtection.isLocked
      ? null
      : await resolveContentfulJsonLdFromPageData(pageData, path)
    const recommendedReads = passwordProtection.isLocked
      ? null
      : await resolvePublishedRecommendedReads(path, preview)

    // Process data
    const data = passwordProtection.isLocked
      ? {
          contentType: pageData.model.contentType,
          key: pageData.model.key,
          title: pageData.model.title,
          metaTitle: pageData.model.metaTitle,
          metaDescription: pageData.model.metaDescription,
          updatedAt: pageData.model.updatedAt,
          page: {
            path: pageData.path,
            category: pageData.category,
            updatedAt: pageData.updatedAt,
          },
          fullPath: path,
          passwordProtection,
        }
      : {
          ...pageData.model,
          ...(contentfulJsonLdSchemas
            ? { jsonLdSchemas: contentfulJsonLdSchemas }
            : {}),
          page: { ...pageData },
          recommendedReads,
          fullPath: path,
          banner: {},
          trustMarkersCombination,
          trustPilotInfo: {
            rating: tpRating,
            link: tpLink,
            totalReview: tpTotal,
          },
          testimonials:
            reviews[pageData.model?.state?.state?.toLowerCase()] || [],
          ...(passwordProtection.isProtected ? { passwordProtection } : {}),
        }

    // Handle geo pages
    if (
      pageData?.path === 'debt-consolidation-near-me/' ||
      data.contentType === 'wwwDebtConsolidationStatesCities'
    ) {
      data.jsonLdId =
        pageData?.path === 'debt-consolidation-near-me/'
          ? 'geo-landing-page'
          : data.city
          ? 'geo-city-page'
          : 'geo-state-page'
    }

    if (
      !passwordProtection.isLocked &&
      (data.contentType === CONTENT_TYPES.ARTICLE_PAGES ||
        data.contentType === CONTENT_TYPES.BLOG_PAGES)
    ) {
      try {
        const slimAuthors =
          await AuthorsService.getInstance().getSlimAuthorsByIds(
            [data.author?.key, data.reviewedBy?.key],
            preview
          )
        if (data.author?.key && slimAuthors[data.author.key]) {
          data.author = slimAuthors[data.author.key]
        }

        if (data.reviewedBy?.key && slimAuthors[data.reviewedBy.key]) {
          data.reviewedBy = slimAuthors[data.reviewedBy.key]
        }
      } catch (error) {
        logger.error('DynamicPage: failed to fetch slim authors', {
          path,
          error,
        })
      }
    }

    if (
      !passwordProtection.isLocked &&
      (data.contentType === CONTENT_TYPES.ARTICLE_PAGES ||
        data.contentType === CONTENT_TYPES.BLOG_PAGES)
    ) {
      data.page = {
        category: data.page.category,
        path: data.page.path,
        updatedAt: data.page.updatedAt,
      }
    }

    // Generate SEO data
    const seoData = generateSeoData({
      title: data.metaTitle,
      description: data.metaDescription,
      url: canonicalUrl,
      lastUpdate: data.updatedAt,
      follow: passwordProtection.isProtected ? false : data.follow,
      index: passwordProtection.isProtected ? false : data.index,
      images: data.imageAsset
        ? [
            {
              url: data.imageAsset.url,
              width: data.imageAsset.details.image.width,
              height: data.imageAsset.details.image.height,
              alt: data.imageAsset.fileName,
            },
          ]
        : [],
    })

    megamenuData = await getMegamenuData(context)

    return {
      props: { data, locale, seoData, megamenuData: megamenuData || null },
    }
  } catch (error) {
    logger.error('DynamicPage: failed to get serverside props', {
      pid: params.pid,
      path,
      preview,
      error,
    })

    return { notFound: true }
  }
})

const DynamicPage = ({ data, seoData, locale, megamenuData }) => {
  const Component = layoutsByType[data.contentType]

  return (
    <Layout
      footerClassName="max-w-1400"
      locale={locale}
      isArticleOrBlogPage
      isNonBrandPage={NON_BRAND_CONTENT_TYPES.includes(data.contentType)}
      megamenuData={megamenuData}
    >
      <WithSeoOverrides {...seoData} />
      {!data.passwordProtection?.isLocked && (
        <ContentfulJsonLdBlocks jsonLdSchemas={data?.jsonLdSchemas} />
      )}
      {data.passwordProtection?.isLocked ? (
        <PasswordProtectedGate entryId={data.passwordProtection.entryId} />
      ) : (
        <Component {...data} locale={locale} />
      )}
    </Layout>
  )
}

DynamicPage.propTypes = {
  data: PropTypes.shape({
    contentType: PropTypes.string.isRequired,
    page: PropTypes.shape({
      path: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  locale: PropTypes.oneOf(['en', 'es']).isRequired,
  seoData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
}

export default React.memo(DynamicPage)
